import React from 'react';
import { Box, styled } from '@mui/material';
import { MuiMap } from 'lib/MuiMap';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { ApplicationState } from 'store/store';
import { setCoordinates, getLocations } from 'store/locations/locationsSlice';
import logger from 'lib/logger';
import { isNumber, isString } from 'lib/typeInference';
import { addressApi, SuggestionResponseItem } from '../addressApi';
import { IAddress } from '@pepperhq/types-sdk';
import { BottomDialog } from 'lib/BottomDialog';
import { AddressSuggestionList } from './AddressSuggestionList';
import { AddressMapCrosshair } from './AddressMapCrosshair';
import { ButtonV2 } from 'ui/ButtonV2';
import { FlatAppbar } from 'ui/FlatAppbar';
import { FlatToolbar } from 'ui/FlatToolbar';
import { ShowAlertFunction } from 'ui/alert/useNotificationAlert';
import { useTranslation } from 'react-i18next';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

interface AddressMapPickerProps {
    onClose: () => void;
    onSelect: (address: IAddress) => void;
    onError: ShowAlertFunction;
}

const classes = {
    map: 'AddressMapPicker-map'
};

const StyledBox = styled(Box)(() => ({
    [`& .${classes.map}`]: {
        width: '100%',
        height: '100%',
        flexShrink: 0
    }
}));

function geoFindMe(onSuccess: (lat: number, lng: number) => void, onError: () => void) {
    function success(position: GeolocationPosition) {
        const { latitude, longitude } = position.coords;
        onSuccess(latitude, longitude);
    }

    if (!navigator.geolocation) {
        console.warn('Geolocation is not supported by your browser');
    } else {
        navigator.geolocation.getCurrentPosition(success, onError);
    }
}

export const AddressMapPicker: React.FC<AddressMapPickerProps> = ({ onClose, onSelect, onError }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { logUserEvent } = useGAHelpers();
    const [map, setMap] = React.useState(null as null | google.maps.Map);
    const [addressList, setAddressList] = React.useState<SuggestionResponseItem[] | null>(null);
    const handleAddressSelect = React.useCallback(
        (item: SuggestionResponseItem) => {
            try {
                const coords = map?.getCenter()?.toJSON();
                if (coords && isNumber(coords.lat) && isNumber(coords.lng)) {
                    onSelect({ coordinates: [coords.lng, coords.lat], address: item });
                } else {
                    throw new Error('Missing coordinates');
                }
            } catch (e: any) {
                logger.error(e);
                onError(
                    'error',
                    t('DELIVERY_MAP_CANNOT_FIND_ADDRESS_FOR_COORDINATES'),
                    '',
                    t('DELIVERY_ADD_ADDRESS_MANUALLY')
                );
                onClose();
            }
        },
        [map, onClose, onError, onSelect, t]
    );
    const handleSubmit = React.useCallback(async () => {
        try {
            logUserEvent(EGAEventName.AddressEntryFromMapPinSelected);
            const coords = map?.getCenter()?.toJSON();
            if (coords && isNumber(coords.lat) && isNumber(coords.lng)) {
                const result = await addressApi.reverseGeocode([coords.lng, coords.lat]);
                const addresses = result.items?.filter(
                    item =>
                        isString(item.line1) &&
                        isString(item.country) &&
                        isString(item.city) &&
                        isString(item.postalCode)
                );
                if (addresses.length === 1) {
                    onSelect({ coordinates: [coords.lng, coords.lat], address: addresses[0] });
                } else if (addresses.length > 1) {
                    setAddressList(addresses);
                } else {
                    throw new Error('No address found');
                }
            } else {
                throw new Error('Missing coordinates');
            }
        } catch (e: any) {
            console.error(e);
            onError(
                'error',
                t('DELIVERY_MAP_CANNOT_FIND_ADDRESS_FOR_COORDINATES'),
                '',
                t('DELIVERY_ADD_ADDRESS_MANUALLY')
            );
            onClose();
        }
    }, [logUserEvent, map, onClose, onError, onSelect, t]);
    const handleManualEntry = React.useCallback(() => {
        logUserEvent(EGAEventName.AddressEntryManualSelected);
        onClose();
    }, [logUserEvent, onClose]);
    const { lat, lng } = useSelector((state: ApplicationState) => state.locations);
    React.useEffect(() => {
        geoFindMe(
            (lattitude, longtitude) => {
                dispatch(setCoordinates({ lat: lattitude, lng: longtitude }));
            },
            () => {
                logger.warn('Error during accessing coords');
            }
        );
        dispatch(getLocations());
    }, [dispatch]);
    const mapCenter = React.useMemo(() => {
        if (lat && lng) {
            return { lat, lng };
        }
        return { lng: -2.547855, lat: 54.00366 };
    }, [lat, lng]);
    const handleMapLoad = React.useCallback((mapInstance: google.maps.Map) => {
        setMap(mapInstance);
    }, []);
    const handleAddressListClose = React.useCallback(() => {
        setAddressList(null);
    }, []);
    return (
        <Box height="100%" width="100%" className="hidden-scroll" display="flex" flexDirection="column">
            <FlatAppbar variant="dense">
                <FlatToolbar
                    title={t('DELIVERY_MAP_ADDRESS_SUGGESTIONS_TITLE')}
                    hideUserAvatar
                    disableEdit
                    onBack={onClose}
                />
            </FlatAppbar>
            <StyledBox sx={{ position: 'relative', flexGrow: 10 }}>
                <MuiMap
                    center={mapCenter}
                    width={0}
                    className={classes.map}
                    zoom={lat && lng ? 16 : 5}
                    options={{ disableDefaultUI: true, gestureHandling: 'greedy' }}
                    onMapLoad={handleMapLoad}
                />
                <AddressMapCrosshair />
            </StyledBox>
            <Box flex={1} display="flex" alignContent="flex-end" flexDirection="column" width="100%" p={1}>
                <ButtonV2 variant="contained" fullWidth onClick={handleSubmit}>
                    {t('BUTTON_SUBMIT')}
                </ButtonV2>
                <ButtonV2
                    sx={{ mt: 0 }}
                    variant="text"
                    fullWidth
                    size="small"
                    onClick={handleManualEntry}
                    color="secondary"
                >
                    {t('DELIVERY_ADD_ADDRESS_MANUALLY')}
                </ButtonV2>
            </Box>
            <BottomDialog open={!!addressList} onClose={handleAddressListClose}>
                <AddressSuggestionList list={addressList} onSelect={handleAddressSelect} />
            </BottomDialog>
        </Box>
    );
};
